import { BrainPage } from '../features/brain/Brain';
import { DirectionPage } from '../features/brain/Direction';
import { useEffect, useState, type ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { ThemeProvider } from "../theme/ThemeProvider";
import { createQueryClient } from "./queryClient";
import { isLoggedIn, saveLogin } from "../api/session";
import type { User, Tenant } from "../api/types";

import { sessionEvents, SESSION_EXPIRED } from "../api/client";
import { LoginPage } from "../features/auth/LoginPage";
import { DevicePage } from "../features/device/DevicePage";
import { Shell } from "../features/shell/Shell";
import { ChatPage, NewChatPage } from "../features/chat/pages";
import { KbListPage, KbPage } from "../features/kb/pages";
import { ThinkSessionPage } from "../features/think/pages";
import { ResearchPage } from "../features/research/pages";
import {
  WriteDraftPage,
  WriteEntryPage,
  WriteHomePage,
} from "../features/write/pages";
import { InsightsPage } from "../features/insights/pages";
import { WikiPageRoute } from "../features/wiki/pages";
import { AgentEditPage, AgentsPage } from "../features/agents/pages";
import { SettingsPage } from "../features/settings/pages";
import { TasksPage } from "../features/tasks/pages";
import { ProjectAgentsPage } from "../features/project-agents/pages";
import { ProjectsPage } from "../features/projects/pages";
import { CalendarPage } from "../features/calendar/pages";
import { CompanyRulesPage } from "../features/platform-settings/CompanyRules";
import { PlatformSettingsPage } from "../features/platform-settings/pages";
import { AiActivity } from "../features/focus/AiActivity";
import { FocusPage } from "../features/focus/pages";
import { InboxPage } from "../features/inbox/pages";
import { InspirationPage } from "../features/inspiration/pages";
import { ApprovalsPage } from "../features/approvals/pages";
import { OkrPage } from "../features/okr/pages";
import { CustomersPage } from "../features/customers/pages";
import { DecisionsPage } from "../features/decisions/pages";

import {
  DashboardPage,
  PersonalHomePage,
  HomeDefaultsPage,
} from "../features/home";
import { AppsPage, AdminAppsOverview } from "../features/apps";

/** 没登录就去登录页；登录过期（刷新令牌也换不到）时回登录页，并记下回来的地址。 */
function RequireLogin({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const onExpired = () =>
      navigate("/login", {
        replace: true,
        state: { from: location.pathname + location.search, expired: true },
      });
    sessionEvents.addEventListener(SESSION_EXPIRED, onExpired);
    return () => sessionEvents.removeEventListener(SESSION_EXPIRED, onExpired);
  }, [navigate, location]);
  const hash = new URLSearchParams(location.hash.slice(1));
  const encoded = hash.get("oidc_result");
  if (encoded) {
    try {
      const raw = atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));
      const r = JSON.parse(
        new TextDecoder().decode(Uint8Array.from(raw, (c) => c.charCodeAt(0))),
      ) as {
        token: string;
        refresh_token?: string;
        user: User;
        tenant?: Tenant;
      };
      if (r.token && r.user) {
        saveLogin({
          token: r.token,
          refreshToken: r.refresh_token,
          user: r.user,
          tenant: r.tenant,
        });
        window.history.replaceState(null, "", window.location.pathname);
      }
    } catch {
      /* 登录页会保留可重试入口 */
    }
  }
  if (!isLoggedIn())
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  return <>{children}</>;
}

/** 旧界面的资料链接（迁移来的文档、判决书和分歧地图里都有）：跳到新的知识库页并打开那一篇。 */
function LegacyKb() {
  const { kbId = "" } = useParams();
  const [params] = useSearchParams();
  const doc = params.get("knowledge_id");
  return (
    <Navigate
      to={`/kb/${encodeURIComponent(kbId)}${doc ? `?doc=${encodeURIComponent(doc)}&kb=${encodeURIComponent(kbId)}` : ""}`}
      replace
    />
  );
}

/** 思考没有单独的首页了：/think 换成新对话，「思考讨论」开着，带来的问题和资料照样放进去。 */
function ThinkStart() {
  const location = useLocation();
  return (
    <Navigate
      to="/ask"
      replace
      state={{ ...(location.state as object | null), mode: "think" }}
    />
  );
}

function Home() {
  const location = useLocation();
  return location.state &&
    ("mode" in location.state ||
      "topic" in location.state ||
      "scope" in location.state) ? (
    <Navigate to="/ask" replace state={location.state} />
  ) : (
    <FocusPage />
  );
}
function EntityLink({ base, param }: { base: string; param: string }) {
  const { entityId = "" } = useParams();
  return (
    <Navigate replace to={`${base}?${param}=${encodeURIComponent(entityId)}`} />
  );
}
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/device"
        element={
          <RequireLogin>
            <DevicePage />
          </RequireLogin>
        }
      />
      <Route
        element={
          <RequireLogin>
            <Shell />
          </RequireLogin>
        }
      >
        <Route index element={<Home />} />
        <Route path="home" element={<FocusPage />} />
        <Route path="personal-home" element={<PersonalHomePage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="apps" element={<AppsPage />} />
        <Route path="apps/admin" element={<AdminAppsOverview />} />
        <Route path="settings/platform/home" element={<HomeDefaultsPage />} />
        <Route
          path="ai-actions"
          element={
            <section className="work-page">
              <h1>AI 动作账本</h1>
              <AiActivity />
            </section>
          }
        />
        <Route path="focus" element={<Navigate to="/" replace />} />
        <Route
          path="tasks/:entityId"
          element={<EntityLink base="/tasks" param="task" />}
        />
        <Route
          path="projects/:entityId"
          element={<EntityLink base="/projects" param="project" />}
        />
        <Route
          path="approvals/:entityId"
          element={<EntityLink base="/approvals" param="approval" />}
        />
        <Route path="posts/:entityId" element={<InspirationPage />} />
        <Route
          path="customers/:entityId"
          element={<EntityLink base="/customers" param="customer" />}
        />
        <Route
          path="decisions/:entityId"
          element={<EntityLink base="/decisions" param="decision" />}
        />
        <Route path="ask" element={<NewChatPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="direction" element={<DirectionPage />} />
        <Route path="brain" element={<BrainPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="project-agents" element={<ProjectAgentsPage />} />
        <Route path="project-agents/:id" element={<ProjectAgentsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="inspiration" element={<InspirationPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="okr" element={<OkrPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="decisions" element={<DecisionsPage />} />
        <Route path="platform-settings" element={<PlatformSettingsPage />} />
        <Route path="settings/platform" element={<PlatformSettingsPage />} />
        <Route
          path="settings/platform/company-rules"
          element={<CompanyRulesPage />}
        />
        <Route
          path="research"
          element={<Navigate to="/ask" replace state={{ mode: "research" }} />}
        />
        <Route path="chat/:sessionId" element={<ChatPage />} />
        <Route path="think" element={<ThinkStart />} />
        <Route path="think/:thinkId" element={<ThinkSessionPage />} />
        <Route path="research/:researchId" element={<ResearchPage />} />
        <Route path="write" element={<WriteHomePage />} />
        <Route path="write/:draftId" element={<WriteDraftPage />} />
        <Route path="write/entry/:entryId" element={<WriteEntryPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="agents/:agentId" element={<AgentEditPage />} />
        <Route path="settings" element={<SettingsPage />} />
        {/* 旧界面的地址：不再有旧页面了 */}
        <Route path="platform/knowledge-bases/:kbId" element={<LegacyKb />} />
        <Route path="platform/*" element={<Navigate to="/" replace />} />
        <Route path="kb" element={<KbListPage />} />
        <Route path="kb/:kbId" element={<KbPage />} />
        <Route path="kb/:kbId/d/:docId" element={<KbPage />} />
        <Route path="kb/:kbId/wiki/*" element={<WikiPageRoute />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function Providers({
  children,
  client,
}: {
  children: ReactNode;
  client?: QueryClient;
}) {
  const [qc] = useState(() => client ?? createQueryClient());
  return (
    <QueryClientProvider client={qc}>
      <ThemeProvider>{children}</ThemeProvider>
    </QueryClientProvider>
  );
}

export function App() {
  return (
    <Providers>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <AppRoutes />
      </BrowserRouter>
    </Providers>
  );
}
